/**
 * O que o HUD mostra, como dado puro. Sem React, sem DOM, sem three.
 *
 * Aqui mora a decisão que importa (REV-16): cada número é comparado ao ORÇAMENTO
 * do tier vigente, vindo de `PERF.yaml`. Um HUD que só mostra número não diz se
 * está bom — quem sabe o limite de cor é este arquivo, e é ele que tem teste.
 *
 * NEVER escreva limite aqui: todo valor de comparação vem de `@game/gamedata/perf`.
 */
import { INVARIANT_BUDGETS, TIER_BUDGETS, type TierKey } from '@game/gamedata/perf';
import type { FrameStats } from '../perf/frame-sampler.js';

export type HudStatus = 'ok' | 'warn' | 'fail';

export interface HudRow {
  readonly label: string;
  readonly value: string;
  readonly status: HudStatus;
}

export interface HudInput {
  readonly stats: FrameStats;
  readonly tier: TierKey;
  readonly renderScale: number;
  /** `undefined` antes do primeiro `pong`. */
  readonly rttMs: number | undefined;
  readonly players: number;
  /**
   * Distância que a última reconciliação corrigiu, em unidades de mundo.
   * `undefined` com a predição desligada ou antes do primeiro snapshot.
   */
  readonly reconErrorUnits: number | undefined;
  /**
   * Atraso com que os OUTROS jogadores estão sendo desenhados, em ms.
   * `undefined` com a interpolação desligada ou antes do primeiro snapshot.
   */
  readonly interpBufferMs: number | undefined;
  /**
   * Atraso que o cliente está PEDINDO. Anda com o jitter medido, entre `BUD-interp` e
   * `BUD-interp-max`, e é contra ELE que o atraso real tem sentido — comparar com o
   * piso fixo pintaria de amarelo uma rede ruim que o cliente já compensou certo.
   */
  readonly interpTargetMs: number | undefined;
  /**
   * Vezes que o buffer de interpolação secou e teve que ser refeito. Cada uma é um
   * solavanco que o jogador VIU. Acumula na sessão de propósito: o que interessa em
   * aparelho real é "aconteceu quantas vezes", NEVER "está acontecendo agora".
   */
  readonly interpReanchors: number | undefined;
  /** Inputs jogados fora por rede pior que `BUD-tolerated-rtt`. Acumula na sessão. */
  readonly droppedInputs: number | undefined;
  /**
   * A base de tempo em que o CORPO está sendo desenhado agora (`ADR-0017`), que é o
   * que aquele ADR pediu como consequência: com três bases na mesma tela, todo "o
   * objeto pulou" passa a ter três suspeitos, e diagnosticar exige saber qual valia.
   *
   * `undefined` fora de partida em rede — na sala local NEVER existem duas bases.
   */
  readonly bodyBase: 'prev' | 'interp' | undefined;
  /**
   * Costuras de base armadas na sessão, e o tamanho da última em unidades de mundo.
   * O tamanho é o salto que o jogador teria visto sem a mistura, e é ele dividido por
   * `BUD-body-handoff` que vira a velocidade extra na tela.
   */
  readonly bodySeams: number | undefined;
  readonly bodySeamUnits: number | undefined;
  /** Costura ainda sendo paga, em ms. Acima de zero, tem objeto assentando na tela. */
  readonly bodySettlingMs: number | undefined;
  /**
   * Por que o campo magnético caiu, acumulado na sessão.
   *
   * Está no HUD porque o defeito de "segurei e soltou" vive em APARELHO e NEVER
   * se reproduz em emulador — sem esta linha, consertar é chutar. `up` sem o dedo
   * ter levantado acusa o toque; `cancel` acusa o browser tomando o ponteiro;
   * `blur` acusa a aba perdendo o foco.
   */
  readonly magnetReleases: Readonly<{ up: number; cancel: number; blur: number }>;
}

/** Sem amostra ainda, ou sem medida: NEVER invente número, mostre que não sabe. */
const UNKNOWN = '—';

function frameStatus(ms: number, tier: TierKey): HudStatus {
  const budget = TIER_BUDGETS['BUD-frame-time'][tier];
  if (ms <= budget.target) return 'ok';
  if (ms <= budget.fail) return 'warn';
  return 'fail';
}

function ms(value: number): string {
  return `${value.toFixed(1)} ms`;
}

export function hudRows(input: HudInput): HudRow[] {
  const { stats, tier } = input;
  const measured = stats.count > 0;

  // p95 E pior 1%, NEVER só a média: com exatamente 5% de picos o p95 cai no
  // último frame bom e esconde a travada que o jogador sente (DIS-09).
  const rows: HudRow[] = [
    {
      label: 'frame p95',
      value: measured ? ms(stats.p95Ms) : UNKNOWN,
      status: measured ? frameStatus(stats.p95Ms, tier) : 'ok',
    },
    {
      label: 'pior 1%',
      value: measured ? ms(stats.worst1PercentMs) : UNKNOWN,
      status: measured ? frameStatus(stats.worst1PercentMs, tier) : 'ok',
    },
  ];

  const tolerated = INVARIANT_BUDGETS['BUD-tolerated-rtt'].value;
  rows.push({
    label: 'rtt',
    // Dois estados de propósito: o orçamento dá UM limite, e inventar a faixa de
    // atenção seria inventar número fora do `PERF.yaml`.
    value: input.rttMs === undefined ? UNKNOWN : `${String(Math.round(input.rttMs))} ms`,
    status: input.rttMs !== undefined && input.rttMs > tolerated ? 'fail' : 'ok',
  });

  // O número que diz se a predição está honesta. Alto significa que o servidor
  // discordou feio do palpite, e é isso que o jogador sente como teleporte.
  const reconBudget = INVARIANT_BUDGETS['BUD-recon-error'].value;
  rows.push({
    label: 'recon',
    value:
      input.reconErrorUnits === undefined ? UNKNOWN : `${input.reconErrorUnits.toFixed(2)} u`,
    status:
      input.reconErrorUnits !== undefined && input.reconErrorUnits > reconBudget ? 'fail' : 'ok',
  });

  // O atraso com que os outros aparecem, contra o que o cliente está PEDINDO — o alvo
  // adapta com o jitter, então comparar com o piso fixo acusaria de problema uma rede
  // ruim já compensada. O valor de regime oscila entre o alvo e ele menos um intervalo
  // de snapshot; essa folga é a cadência da rede, NEVER um defeito. Zero é buffer
  // SECO: a tela dos outros congelou.
  const snapshotIntervalMs = 1000 / INVARIANT_BUDGETS['BUD-snapshot'].value;
  const buffer = input.interpBufferMs;
  const target = input.interpTargetMs ?? INVARIANT_BUDGETS['BUD-interp'].value;
  rows.push({
    label: 'interp',
    // Mostra os dois quando o alvo saiu do piso: só o número de baixo NEVER diria que
    // o cliente já subiu o buffer de propósito, e é essa a informação que interessa
    // em aparelho real.
    value:
      buffer === undefined
        ? UNKNOWN
        : target > INVARIANT_BUDGETS['BUD-interp'].value
          ? `${String(Math.round(buffer))}/${String(Math.round(target))} ms`
          : `${String(Math.round(buffer))} ms`,
    status:
      buffer === undefined || buffer > target - snapshotIntervalMs
        ? 'ok'
        : buffer > 0
          ? 'warn'
          : 'fail',
  });

  // Contadores de dor acumulada. Qualquer coisa acima de zero é a rede tendo passado
  // do que os orçamentos cobrem — sintoma, NEVER erro de programação. Ficam em
  // atenção e NEVER em falha: quem falha é o orçamento, e ele já tem linha própria.
  rows.push({
    label: 'solavancos',
    value: input.interpReanchors === undefined ? UNKNOWN : String(input.interpReanchors),
    status: input.interpReanchors !== undefined && input.interpReanchors > 0 ? 'warn' : 'ok',
  });
  rows.push({
    label: 'perdidos',
    value: input.droppedInputs === undefined ? UNKNOWN : String(input.droppedInputs),
    status: input.droppedInputs !== undefined && input.droppedInputs > 0 ? 'warn' : 'ok',
  });

  // `cancel` é o único dos três que acusa defeito: significa que o browser tomou
  // o ponteiro do jogador no meio da jogada. `up` e `blur` são o jogador agindo.
  const releases = input.magnetReleases;
  rows.push({
    label: 'ímã u/c/b',
    value: `${String(releases.up)}/${String(releases.cancel)}/${String(releases.blur)}`,
    status: releases.cancel > 0 ? 'fail' : 'ok',
  });

  // A base de tempo do corpo, e o preço da última troca dela. Duas linhas porque são
  // duas perguntas diferentes: "de onde vem o que estou vendo?" e "quanto custou a
  // última vez que isso mudou?". O tamanho fica em unidades, como o `recon`, porque é
  // a mesma grandeza — distância que a tela teve que corrigir.
  rows.push({
    label: 'corpo',
    value: input.bodyBase ?? UNKNOWN,
    // NEVER tem status: as duas bases são CERTAS, cada uma no seu caso. Pintar uma
    // delas de amarelo diria que o jogo está errado quando ele está fazendo o que o
    // `ADR-0017` decidiu.
    status: 'ok',
  });
  rows.push({
    label: 'costura',
    value:
      input.bodySeams === undefined || input.bodySeamUnits === undefined
        ? UNKNOWN
        : `${String(input.bodySeams)} · ${input.bodySeamUnits.toFixed(2)} u`,
    // Atenção enquanto está sendo paga, e NEVER por tamanho: NEVER existe orçamento de
    // salto de troca de base, e inventar um limite aqui seria escrever número fora do
    // `PERF.yaml`. Quem julga o tamanho é o olho no aparelho — é essa a pergunta
    // aberta de `BUD-body-handoff`.
    status: input.bodySettlingMs !== undefined && input.bodySettlingMs > 0 ? 'warn' : 'ok',
  });

  rows.push({
    label: 'escala',
    value: `${String(Math.round(input.renderScale * 100))}%`,
    // Escala abaixo do teto é o controlador funcionando, NEVER um problema:
    // o problema apareceria no frame p95, que já está acima.
    status: 'ok',
  });

  rows.push({ label: 'tier', value: tier, status: 'ok' });
  rows.push({ label: 'jogadores', value: String(input.players), status: 'ok' });

  return rows;
}
